import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BackButton, InlineFeedback, PrimaryButton } from 'components/forms';
import config from 'config';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useGetClientSalesQuery, useSyncClientSaleToCrmMutation } from 'services/api/clients.api';
import priceFormat from 'services/format/priceFormat';
import type { CrmStateField, InsuranceType, Sale, SaleCrmState, SaleExternalDuplicate } from 'services/types/sale';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  STATUS_GOOD,
  STATUS_NEUTRAL,
  STATUS_WARN,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';
import { getProgressColor, getProgressTextColor } from 'utils/getAdvancePercentage';
import { normalizeNationalityToIso2 } from 'utils/normalizeNationality';

import {
  CRM_FIELD_CATALOG,
  CRM_FIELD_GROUPS,
  CRM_PRIMARY_FIELDS,
  formatCrmDateOnly,
  orderCrmChangeKeys,
} from './crmStateFields';
import InfoItem from './InfoItem';
import SaleCreateForm from './SaleCreateForm';
import SaleEditForm from './SaleEditForm';
import { type SaleCreatePrefill, subscribeToSaleCreatePrefill } from './saleCreatePrefillBus';

interface SalesSectionProps {
  clientId: number;
  clientRut?: string | null;
  clientGender?: string | null;
  clientDateOfBirth?: string | null;
  clientNationality?: string | null;
  clientEmail?: string | null;
  clientAddress?:
    | {
        street?: string | null;
        number?: string | null;
        commune?: string | null;
      }
    | string
    | null;
}

const insuranceTypeLabels: Record<InsuranceType, string> = {
  loanProtectionInsurance: 'Desgravamen',
  unemploymentInsurance: 'Cesantía',
};

const formatDateTime = (value?: string | Date): string | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const datePart = date.toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return `${datePart}, ${timePart}`;
};

// Shared by the current-state grid and the history entries so both render a field the same way.
const renderCrmStateValue = (key: CrmStateField, raw?: string | null): React.ReactNode | undefined => {
  if (!raw) return undefined;
  switch (CRM_FIELD_CATALOG[key].format) {
    case 'currency': {
      const amount = Number(raw);
      return Number.isNaN(amount) ? raw : priceFormat.format(amount);
    }
    case 'boolean':
      return raw === 'true' ? 'Sí' : 'No';
    case 'date':
      return formatCrmDateOnly(raw);
    case 'dateTime':
      return formatDateTime(raw);
    case 'url':
      return (
        <Typography
          variant="body2"
          component="a"
          href={raw}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: NAVY,
            fontWeight: 600,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Abrir
        </Typography>
      );
    default:
      return raw;
  }
};

const documentTypeLabels: Record<string, string> = {
  identity_card: 'Cédula de Identidad',
  loan: 'Crédito',
  insurance_policy: 'Póliza de Seguro',
  bank_statement: 'Cartola Bancaria',
  debt_settlement_report: 'Liquidación de Deudas',
  salary_settlement_report: 'Liquidación de Salario',
  cmf_debt_report: 'Reporte CMF',
  afp_contribution_certificate: 'Certificado de Cotizaciones AFP',
  utility_bill: 'Boleta de Servicio',
  additional_document: 'Documento Adicional',
  other: 'Otro',
};

const labelDocumentType = (type: string | null): string => {
  if (!type) return 'Documento';
  return documentTypeLabels[type] ?? type;
};

const createInfoItem = (label: string, value: React.ReactNode | undefined | null, fullWidth = false) => {
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;
  return <InfoItem key={label} label={label} value={displayValue} fullWidth={fullWidth} />;
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 600,
      mt: 1,
      mb: 1,
      color: NAVY,
    }}
  >
    {title}
  </Typography>
);

// Same chrome as the "Historial de estados" accordion below, so the Salesforce block reads
// as one stack of peers. Renders nothing when Salesforce sent no value for any of its fields.
const CrmStateGroup: React.FC<{ title: string; fields: readonly CrmStateField[]; crmState: SaleCrmState }> = ({
  title,
  fields,
  crmState,
}) => {
  const filled = fields.filter((key) => {
    const raw = crmState[key];
    return raw !== null && raw !== undefined && raw !== '';
  });
  if (filled.length === 0) return null;

  return (
    <Accordion
      disableGutters
      sx={{ boxShadow: 'none', backgroundColor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 0, minHeight: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}
      >
        <Typography variant="body2" sx={{ color: NAVY, fontWeight: 600 }}>
          {title} ({filled.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Grid container spacing={0.5}>
          {filled.map((key) => createInfoItem(CRM_FIELD_CATALOG[key].label, renderCrmStateValue(key, crmState[key])))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const isSalesforceExternalId = (id?: string | null): boolean => !!id && /[a-zA-Z]/.test(id);
const isSaleCrmSyncable = (sale: Sale): boolean =>
  !sale.externalDuplicate && (!sale.externalId || isSalesforceExternalId(sale.externalId));

/**
 * Insurex already had this portabilidad loaded in Monday, so Relif never created
 * it in Salesforce. Executives need the Monday id to find the case there, which
 * is why it is spelled out rather than just flagged.
 */
const ExternalDuplicateNotice: React.FC<{ duplicate: SaleExternalDuplicate }> = ({ duplicate }) => {
  const details = [
    duplicate.leadStatus ? `Estado: ${duplicate.leadStatus}` : null,
    duplicate.leadCreationDate ? `Creada el ${formatDateTime(duplicate.leadCreationDate)}` : null,
  ].filter(Boolean);

  return (
    <InlineFeedback severity="warning" icon={<CloudOffIcon fontSize="small" />} sx={{ mb: 1.5 }}>
      Esta portabilidad ya estaba cargada en Monday, el sistema interno de Insurex, así que no se subió a Salesforce.
      {duplicate.leadId ? ` ID en Monday: ${duplicate.leadId}.` : ' Insurex no informó el ID del caso.'}
      {details.length > 0 ? ` ${details.join(' · ')}.` : ''}
    </InlineFeedback>
  );
};

const computeMissingSaleFieldsForCrm = (sale: Sale): string[] => {
  const missing: string[] = [];
  if (!sale.insuranceType) missing.push('Tipo de Oportunidad');
  if (!sale.loanData?.loanNumber?.trim()) missing.push('Número de Operación');
  if (sale.loanData?.totalAskedAmount == null) missing.push('Monto Solicitado');
  if (sale.loanData?.pendingBalance == null) missing.push('Monto por Pagar');
  if (sale.loanData?.remainingQuotas == null) missing.push('Cuotas por Pagar');
  if (sale.insuranceData?.refundAmount == null) missing.push('Monto de Devolución');
  return missing;
};

const SalesSection: React.FC<SalesSectionProps> = ({
  clientId,
  clientRut = null,
  clientGender = null,
  clientDateOfBirth = null,
  clientNationality = null,
  clientEmail = null,
  clientAddress = null,
}) => {
  const { data: salesData, isLoading: isLoadingSales } = useGetClientSalesQuery(String(clientId), {
    skip: !clientId,
  });
  const sales = salesData?.sales;

  const missingClientFieldsForCrm = useMemo(() => {
    const missing: string[] = [];
    if (!clientRut?.trim()) missing.push('RUT');
    if (!clientGender?.trim()) missing.push('Sexo');
    if (!clientDateOfBirth?.trim()) missing.push('Fecha de Nacimiento');
    if (!normalizeNationalityToIso2(clientNationality)) missing.push('Nacionalidad');
    if (!clientEmail?.trim()) missing.push('Email');
    const addressObj = clientAddress && typeof clientAddress === 'object' ? clientAddress : null;
    const addressOk = !!addressObj?.street?.trim() && !!addressObj?.number?.trim() && !!addressObj?.commune?.trim();
    if (!addressOk) missing.push('Dirección');
    return missing;
  }, [clientRut, clientGender, clientDateOfBirth, clientNationality, clientEmail, clientAddress]);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<number, boolean>>({});
  const [editingSaleId, setEditingSaleId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<SaleCreatePrefill | null>(null);
  const [syncErrors, setSyncErrors] = useState<Record<number, string>>({});
  const [syncWarnings, setSyncWarnings] = useState<Record<number, string>>({});
  const [syncSaleToCrm, { isLoading: isSyncing, originalArgs: syncingArgs }] = useSyncClientSaleToCrmMutation();
  const { notifySaved } = useFeedback();

  useEffect(() => {
    return subscribeToSaleCreatePrefill((prefill) => {
      if (prefill.clientId !== clientId) return;
      setCreatePrefill(prefill);
      setIsCreating(true);
    });
  }, [clientId]);

  const handleSyncToCrm = async (sale: Sale) => {
    try {
      const result = await syncSaleToCrm({ clientId: String(clientId), saleId: sale.id }).unwrap();
      setSyncErrors((prev) => {
        const next = { ...prev };
        delete next[sale.id];
        return next;
      });

      // Nothing reached Salesforce: the persistent notice rendered from the
      // refetched Sale carries the Monday id, so no transient message is needed.
      if (result.action === 'duplicate') {
        setSyncWarnings((prev) => {
          const next = { ...prev };
          delete next[sale.id];
          return next;
        });
        return;
      }

      const failedDocs = result.documents?.failed ?? [];
      setSyncWarnings((prev) => {
        const next = { ...prev };
        if (failedDocs.length === 0) {
          delete next[sale.id];
          return next;
        }
        const docList = failedDocs.map((d) => labelDocumentType(d.documentType)).join(', ');
        const firstError = failedDocs[0]?.error ?? '';
        next[sale.id] = `La portabilidad se sincronizó con el CRM, pero ${failedDocs.length} ${
          failedDocs.length === 1 ? 'documento no se subió' : 'documentos no se subieron'
        }: ${docList}.${firstError ? ` Detalle: ${firstError}` : ''} Intenta nuevamente para reintentar solo los pendientes.`;
        return next;
      });

      if (failedDocs.length === 0) {
        if (result.action === 'create') {
          notifySaved('Subido al CRM');
        } else if (result.action === 'update') {
          notifySaved('Sincronizado con CRM');
        }
      }
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ?? 'Ocurrió un error al subir al CRM. Intenta más tarde.';
      setSyncErrors((prev) => ({
        ...prev,
        [sale.id]: message,
      }));
    }
  };

  const sortedSales = sales
    ? [...sales].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];

  if (isLoadingSales && !sales) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <PrimaryButton
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
          sx={{ px: 2, py: 0.625, fontSize: '0.8125rem' }}
        >
          Crear portabilidad
        </PrimaryButton>
      </Box>

      {isCreating && (
        <SaleCreateForm
          clientId={String(clientId)}
          prefill={createPrefill}
          onCancel={() => {
            setIsCreating(false);
            setCreatePrefill(null);
          }}
          onCreated={() => {
            setIsCreating(false);
            setCreatePrefill(null);
          }}
        />
      )}

      {sortedSales.length === 0 && !isCreating && (
        <Typography
          sx={{
            color: alpha(NAVY, 0.5),
            fontStyle: 'italic',
          }}
        >
          Este cliente no tiene portabilidades registradas
        </Typography>
      )}

      <Stack spacing={0.5}>
        {sortedSales.map((sale) => {
          const typeLabel = insuranceTypeLabels[sale.insuranceType] ?? sale.insuranceType;
          const institution = sale.loanData?.institutionName?.trim() || 'Sin institución';
          // Surfaced on the collapsed row rather than in the detail: it is how the team identifies
          // a portabilidad against Salesforce, so it has to be readable without expanding.
          const opportunityNumber = sale.externalOpportunityNumber?.trim();
          // The CRM Opportunity / Monday item id lives on the top-level
          // `externalId` column. `insuranceData.externalId` is legacy.
          const isInCrm = !!sale.externalId;
          const balanceLabel = 'Reembolso';
          const balanceValue = sale.insuranceData?.refundAmount;
          const crmState = sale.crmState;

          const missingSaleFields = computeMissingSaleFieldsForCrm(sale);
          const blockedReasons: string[] = [];
          if (missingClientFieldsForCrm.length) {
            blockedReasons.push(`Cliente: ${missingClientFieldsForCrm.join(', ')}`);
          }
          if (missingSaleFields.length) {
            blockedReasons.push(`Venta: ${missingSaleFields.join(', ')}`);
          }
          const canSyncSaleToCrm = blockedReasons.length === 0;
          const saleSyncTooltip = canSyncSaleToCrm
            ? sale.externalId
              ? 'Actualizar en CRM'
              : 'Subir al CRM'
            : `Falta completar — ${blockedReasons.join(' | ')}`;

          return (
            <Accordion
              key={sale.id}
              expanded={expandedAccordions[sale.id] || false}
              onChange={(_, isExpanded) => {
                setExpandedAccordions((prev) => ({
                  ...prev,
                  [sale.id]: isExpanded,
                }));
              }}
              disableGutters
              sx={{
                boxShadow: `0px 1px 4px ${alpha(NAVY, 0.08)}`,
                backgroundColor: SURFACE_CONTAINER_LOWEST,
                borderRadius: RADIUS_CARD,
                '&:first-of-type, &:last-of-type': { borderRadius: RADIUS_CARD },
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
                transition: 'box-shadow 0.2s ease-in-out',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
                sx={{
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  borderRadius: expandedAccordions[sale.id] ? `${RADIUS_CARD} ${RADIUS_CARD} 0 0` : RADIUS_CARD,
                  minHeight: 48,
                  overflow: 'hidden',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 1,
                    overflow: 'hidden',
                    minWidth: 0,
                  },
                  transition: 'background-color 0.2s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Tooltip title={institution} arrow placement="top">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: NAVY,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {institution}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(NAVY, 0.6),
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        {typeLabel}
                      </Typography>
                      {opportunityNumber && (
                        <Tooltip title={`N° de oportunidad en el CRM: ${opportunityNumber}`} arrow placement="top">
                          <Typography
                            variant="caption"
                            sx={{
                              color: alpha(NAVY, 0.75),
                              backgroundColor: alpha(NAVY, 0.06),
                              px: 0.75,
                              py: 0.125,
                              borderRadius: 999,
                              fontWeight: 600,
                              fontSize: '0.6875rem',
                              lineHeight: 1.4,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 140,
                            }}
                          >
                            {opportunityNumber}
                          </Typography>
                        </Tooltip>
                      )}
                      {sale.advancePercentage != null && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: getProgressTextColor(sale.advancePercentage),
                            backgroundColor: getProgressColor(sale.advancePercentage),
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 999,
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                          }}
                          title={`Avance de la portabilidad: ${sale.advancePercentage}%`}
                        >
                          {sale.advancePercentage}%
                        </Typography>
                      )}
                      {sale.referralData && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: NAVY,
                            backgroundColor: alpha(NAVY, 0.08),
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 999,
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                          }}
                          title={
                            sale.referralData.firstName || sale.referralData.lastName
                              ? `Referido: ${[sale.referralData.firstName, sale.referralData.lastName].filter(Boolean).join(' ')}`
                              : 'Referido'
                          }
                        >
                          Referido
                        </Typography>
                      )}
                      {sale.externalDuplicate && (
                        <Chip
                          icon={<CloudOffIcon />}
                          label="En Monday"
                          size="small"
                          title={
                            sale.externalDuplicate.leadId
                              ? `Ya cargada en Monday por Insurex — ID ${sale.externalDuplicate.leadId}`
                              : 'Ya cargada en Monday por Insurex'
                          }
                          sx={{
                            height: 20,
                            bgcolor: alpha(STATUS_WARN, 0.12),
                            color: STATUS_WARN,
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            '& .MuiChip-icon': { color: STATUS_WARN, fontSize: '0.95rem', ml: 0.5 },
                            '& .MuiChip-label': { px: 0.625 },
                          }}
                        />
                      )}
                      {sale.insuranceData?.simulationRecalculated && (
                        <Chip
                          icon={<AutorenewIcon />}
                          label="Recalculada"
                          size="small"
                          title="Simulación recalculada por Sofía — no fue un error de cálculo"
                          sx={{
                            height: 20,
                            bgcolor: alpha(STATUS_WARN, 0.12),
                            color: STATUS_WARN,
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            '& .MuiChip-icon': { color: STATUS_WARN, fontSize: '0.95rem', ml: 0.5 },
                            '& .MuiChip-label': { px: 0.625 },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: isInCrm ? alpha(STATUS_GOOD, 0.55) : STATUS_NEUTRAL,
                        flexShrink: 0,
                      }}
                    />
                    {isSaleCrmSyncable(sale) && (
                      <Tooltip title={saleSyncTooltip} arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSyncToCrm(sale);
                            }}
                            disabled={!canSyncSaleToCrm || (isSyncing && syncingArgs?.saleId === sale.id)}
                            sx={{ color: NAVY }}
                          >
                            {sale.externalId ? (
                              <CloudSyncIcon fontSize="small" />
                            ) : (
                              <CloudUploadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        sale.externalDuplicate
                          ? 'No disponible: la portabilidad se gestiona en Monday'
                          : sale.externalId
                            ? 'Abrir chat con back-office'
                            : 'Disponible una vez subido al CRM'
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!sale.externalId || sale.externalDuplicate) return;
                            const url = `${config.mueveSeguroChatterUrl.replace(/\/$/, '')}/chat/${sale.externalId}`;
                            window.open(
                              url,
                              'mueve-seguro-chatter',
                              'width=520,height=720,scrollbars=yes,resizable=yes'
                            );
                          }}
                          disabled={!sale.externalId || !!sale.externalDuplicate}
                          sx={{ color: NAVY }}
                        >
                          <ChatBubbleOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSaleId(sale.id);
                          setExpandedAccordions((prev) => ({ ...prev, [sale.id]: true }));
                        }}
                        sx={{ color: NAVY }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>
                {editingSaleId === sale.id ? (
                  <SaleEditForm
                    clientId={String(clientId)}
                    sale={sale}
                    onCancel={() => setEditingSaleId(null)}
                    onSaved={() => setEditingSaleId(null)}
                  />
                ) : (
                  <Box>
                    {sale.externalDuplicate && <ExternalDuplicateNotice duplicate={sale.externalDuplicate} />}
                    <SectionHeader title="Información de Salesforce" />
                    <Grid container spacing={0.5} sx={{ mb: 1 }}>
                      {createInfoItem('ID externo (CRM)', sale.externalId)}
                      {createInfoItem('Subido a CRM', formatDateTime(sale.insuranceData?.uploadedAt))}
                      {CRM_PRIMARY_FIELDS.map((key) =>
                        createInfoItem(CRM_FIELD_CATALOG[key].label, renderCrmStateValue(key, crmState?.[key]))
                      )}
                      {createInfoItem('Última actualización', formatDateTime(crmState?.lastEventAt), true)}
                    </Grid>

                    {crmState && (
                      <Box sx={{ mb: 1 }}>
                        {CRM_FIELD_GROUPS.map((group) => (
                          <CrmStateGroup
                            key={group.title}
                            title={group.title}
                            fields={group.fields}
                            crmState={crmState}
                          />
                        ))}
                      </Box>
                    )}

                    {(sale.crmStateHistory?.length ?? 0) > 0 && (
                      <Accordion
                        disableGutters
                        sx={{
                          boxShadow: 'none',
                          backgroundColor: 'transparent',
                          mb: 1,
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ px: 0, minHeight: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                        >
                          <Typography variant="body2" sx={{ color: NAVY, fontWeight: 600 }}>
                            Historial de estados ({sale.crmStateHistory?.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                          {/* Newest first; SF only keeps the current value, this is our applied-event trail. */}
                          <Stack spacing={0.75}>
                            {[...(sale.crmStateHistory ?? [])].reverse().map((entry, idx) => (
                              <Box
                                key={`${entry.at}-${idx}`}
                                sx={{ backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM, p: 1 }}
                              >
                                <Typography variant="caption" sx={{ color: alpha(NAVY, 0.6) }}>
                                  {formatDateTime(entry.at)}
                                </Typography>
                                <Grid container spacing={0.5}>
                                  {orderCrmChangeKeys(entry.changes).map((key) => {
                                    const raw = entry.changes[key as CrmStateField];
                                    const field = CRM_FIELD_CATALOG[key as CrmStateField];
                                    return field
                                      ? createInfoItem(field.label, renderCrmStateValue(key as CrmStateField, raw))
                                      : createInfoItem(key, raw);
                                  })}
                                </Grid>
                              </Box>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    <SectionHeader title="Información del Crédito" />
                    <Grid container spacing={0.5} sx={{ mb: 1 }}>
                      {createInfoItem('Institución', sale.loanData?.institutionName)}
                      {createInfoItem('Número de Crédito', sale.loanData?.loanNumber)}
                      {createInfoItem(
                        'Monto Total Solicitado',
                        sale.loanData?.totalAskedAmount ? priceFormat.format(sale.loanData.totalAskedAmount) : undefined
                      )}
                      {createInfoItem(
                        'Saldo Pendiente',
                        sale.loanData?.pendingBalance ? priceFormat.format(sale.loanData.pendingBalance) : undefined
                      )}
                      {createInfoItem('Cuotas Restantes', sale.loanData?.remainingQuotas)}
                      {createInfoItem(
                        'Valor de la Cuota',
                        sale.loanData?.installmentValue ? priceFormat.format(sale.loanData.installmentValue) : undefined
                      )}
                    </Grid>

                    <SectionHeader title="Información del Seguro" />
                    <Grid container spacing={0.5} sx={{ mb: 1 }}>
                      {createInfoItem('Aseguradora actual', sale.insuranceData?.currentInstitution)}
                      {createInfoItem('Número de Póliza', sale.insuranceData?.policyNumber)}
                      {createInfoItem(balanceLabel, balanceValue ? priceFormat.format(balanceValue) : undefined)}
                    </Grid>

                    {sale.insuranceData?.simulationRecalculated && (
                      <InlineFeedback severity="warning" icon={<AutorenewIcon fontSize="small" />} sx={{ mb: 1 }}>
                        Simulación recalculada por Sofía — el monto se recalculó automáticamente, no corresponde a un
                        error de cálculo.
                      </InlineFeedback>
                    )}

                    {syncErrors[sale.id] && (
                      <InlineFeedback
                        severity="error"
                        sx={{ mt: 1.5 }}
                        action={
                          <BackButton
                            size="small"
                            onClick={() => handleSyncToCrm(sale)}
                            disabled={isSyncing && syncingArgs?.saleId === sale.id}
                          >
                            Reintentar
                          </BackButton>
                        }
                      >
                        {syncErrors[sale.id]}
                      </InlineFeedback>
                    )}

                    {!syncErrors[sale.id] && syncWarnings[sale.id] && (
                      <InlineFeedback
                        severity="warning"
                        sx={{ mt: 1.5 }}
                        action={
                          <BackButton
                            size="small"
                            onClick={() => handleSyncToCrm(sale)}
                            disabled={isSyncing && syncingArgs?.saleId === sale.id}
                          >
                            Reintentar
                          </BackButton>
                        }
                      >
                        {syncWarnings[sale.id]}
                      </InlineFeedback>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
};

export default SalesSection;
